import accountAvatar from "@/assets/figma/top-navigation/account-avatar.png";
import countryAvatar from "@/assets/figma/top-navigation/country-avatar.png";
import platformAvatar from "@/assets/figma/top-navigation/platform-avatar.png";
import storeAvatar from "@/assets/figma/top-navigation/store-avatar.png";
import type { MerryLeftNavigationRouteItemId } from "@/shared/ui/merryLeftNavigationItems";
import type { MerryTopNavigationSession } from "@/shared/ui/MerryTopNavigation";

export interface DesktopShellRouteDefinition {
  activeNavigationItem?: MerryLeftNavigationRouteItemId;
  description: string;
  match?: (pathname: string) => boolean;
  mode?: "content" | "workspace";
  path: string;
  titleMode?: "page" | "shell";
  title: string;
}

export const desktopShellRoutes = {
  chatHistory: {
    description: "搜索、整理并继续当前用户的商户工作对话。",
    path: "/chat-history",
    title: "历史记录",
    titleMode: "page",
  },
  dataOverview: {
    activeNavigationItem: "dataOverview",
    description: "在工作台上打开全店经营数据总览。",
    mode: "workspace",
    path: "/workspace?drawer=store-overview",
    title: "全店数据总览",
  },
  home: {
    activeNavigationItem: "home",
    description: "登录后的工作台内容将在业务模块确定后放置于此。",
    mode: "workspace",
    path: "/workspace",
    title: "工作台",
  },
  myFiles: {
    description: "文件浏览与管理组件将在业务数据接入后放置于此。",
    path: "/my-files",
    title: "我的文件",
  },
  notifications: {
    description: "通知列表组件将在业务数据接入后放置于此。",
    path: "/notifications",
    title: "通知",
  },
} as const satisfies Record<
  MerryLeftNavigationRouteItemId,
  DesktopShellRouteDefinition
>;

export const desktopShellPageRoutes = {
  foundation: {
    activeNavigationItem: "dataOverview",
    description: "生产前端基础组件与工程能力验证页面。",
    path: "/foundation",
    title: "基础能力",
  },
  experienceStrategies: {
    activeNavigationItem: "notifications",
    description: "查看对话中确认生效的经验策略与当前六维经营打法。",
    path: "/experience-strategies",
    title: "经验策略",
    titleMode: "page",
  },
  workspaceConversation: {
    activeNavigationItem: "home",
    description: "可刷新、可定位来源轮次的对话工作区。",
    match: (pathname: string) =>
      /^\/workspace\/conversations\/[^/]+\/?$/u.test(pathname),
    mode: "workspace",
    path: "/workspace/conversations/:conversationId",
    title: "工作台",
    titleMode: "page",
  },
} as const satisfies Record<string, DesktopShellRouteDefinition>;

export type DesktopShellRouteId =
  | MerryLeftNavigationRouteItemId
  | keyof typeof desktopShellPageRoutes;

export const desktopShellPlaceholderSession = {
  account: {
    accessibleLabel: "查看账户积分",
    avatarSource: accountAvatar,
    label: "剩余积分",
    value: "--",
  },
  store: {
    accessibleLabel: "查看店铺状态",
    avatarSources: [countryAvatar, platformAvatar, storeAvatar],
    label: "店铺状态",
    value: "健康",
  },
  type: "authenticated",
} as const satisfies MerryTopNavigationSession;

export const myFilesDrawerLocation = {
  queryKey: "drawer",
  queryValue: "my-files",
  resourceQueryKey: "resource",
} as const;

export const storeOverviewDrawerLocation = {
  chatQueryKey: "temporary_chat",
  chatViewQueryKey: "temporary_chat_view",
  customEndQueryKey: "range_end",
  customStartQueryKey: "range_start",
  drawerQueryKey: "drawer",
  drawerQueryValue: "store-overview",
  rangeQueryKey: "range",
} as const;

export type StoreOverviewRangeKind = "today" | "7d" | "30d" | "custom";

export interface StoreOverviewDrawerSearch {
  chatView: "open" | "collapsed";
  customEnd: string;
  customStart: string;
  isOpen: boolean;
  range: StoreOverviewRangeKind;
  temporaryConversationId: string;
}

const storeOverviewRangeKinds = new Set<StoreOverviewRangeKind>([
  "today",
  "7d",
  "30d",
  "custom",
]);

function hasSingleSearchValue(params: URLSearchParams, key: string) {
  return params.getAll(key).length === 1;
}

export function parseStoreOverviewDrawerSearch(
  search: string,
): StoreOverviewDrawerSearch {
  const params = new URLSearchParams(search);
  const rawRange = params.get(storeOverviewDrawerLocation.rangeQueryKey);
  const range =
    hasSingleSearchValue(params, storeOverviewDrawerLocation.rangeQueryKey) &&
    rawRange &&
    storeOverviewRangeKinds.has(rawRange as StoreOverviewRangeKind)
      ? (rawRange as StoreOverviewRangeKind)
      : "today";
  const chatId = hasSingleSearchValue(
    params,
    storeOverviewDrawerLocation.chatQueryKey,
  )
    ? (params.get(storeOverviewDrawerLocation.chatQueryKey) ?? "")
    : "";

  return {
    chatView:
      hasSingleSearchValue(
        params,
        storeOverviewDrawerLocation.chatViewQueryKey,
      ) &&
      params.get(storeOverviewDrawerLocation.chatViewQueryKey) === "collapsed"
        ? "collapsed"
        : "open",
    customEnd:
      hasSingleSearchValue(
        params,
        storeOverviewDrawerLocation.customEndQueryKey,
      )
        ? (params.get(storeOverviewDrawerLocation.customEndQueryKey) ?? "")
        : "",
    customStart:
      hasSingleSearchValue(
        params,
        storeOverviewDrawerLocation.customStartQueryKey,
      )
        ? (params.get(storeOverviewDrawerLocation.customStartQueryKey) ?? "")
        : "",
    isOpen:
      hasSingleSearchValue(
        params,
        storeOverviewDrawerLocation.drawerQueryKey,
      ) &&
      params.get(storeOverviewDrawerLocation.drawerQueryKey) ===
        storeOverviewDrawerLocation.drawerQueryValue,
    range,
    temporaryConversationId: /^chat_[a-z0-9]+$/u.test(chatId) ? chatId : "",
  };
}

export function openStoreOverviewDrawerSearch(search: string) {
  const params = new URLSearchParams(search);
  params.set(
    storeOverviewDrawerLocation.drawerQueryKey,
    storeOverviewDrawerLocation.drawerQueryValue,
  );
  params.delete(myFilesDrawerLocation.resourceQueryKey);
  if (!params.has(storeOverviewDrawerLocation.rangeQueryKey)) {
    params.set(storeOverviewDrawerLocation.rangeQueryKey, "today");
  }
  return `?${params.toString()}`;
}

export function updateStoreOverviewDrawerSearch(
  search: string,
  update: Partial<
    Pick<
      StoreOverviewDrawerSearch,
      | "chatView"
      | "customEnd"
      | "customStart"
      | "range"
      | "temporaryConversationId"
    >
  >,
) {
  const params = new URLSearchParams(search);
  params.set(
    storeOverviewDrawerLocation.drawerQueryKey,
    storeOverviewDrawerLocation.drawerQueryValue,
  );
  if (update.range) {
    params.set(storeOverviewDrawerLocation.rangeQueryKey, update.range);
  }
  if (update.customStart !== undefined) {
    if (update.customStart) {
      params.set(
        storeOverviewDrawerLocation.customStartQueryKey,
        update.customStart,
      );
    } else {
      params.delete(storeOverviewDrawerLocation.customStartQueryKey);
    }
  }
  if (update.customEnd !== undefined) {
    if (update.customEnd) {
      params.set(
        storeOverviewDrawerLocation.customEndQueryKey,
        update.customEnd,
      );
    } else {
      params.delete(storeOverviewDrawerLocation.customEndQueryKey);
    }
  }
  if (update.temporaryConversationId !== undefined) {
    if (update.temporaryConversationId) {
      params.set(
        storeOverviewDrawerLocation.chatQueryKey,
        update.temporaryConversationId,
      );
    } else {
      params.delete(storeOverviewDrawerLocation.chatQueryKey);
      params.delete(storeOverviewDrawerLocation.chatViewQueryKey);
    }
  }
  if (update.chatView !== undefined) {
    if (update.chatView === "collapsed") {
      params.set(storeOverviewDrawerLocation.chatViewQueryKey, "collapsed");
    } else {
      params.delete(storeOverviewDrawerLocation.chatViewQueryKey);
    }
  }
  return `?${params.toString()}`;
}

export function closeStoreOverviewDrawerSearch(search: string) {
  const params = new URLSearchParams(search);
  params.delete(storeOverviewDrawerLocation.drawerQueryKey);
  params.delete(storeOverviewDrawerLocation.rangeQueryKey);
  params.delete(storeOverviewDrawerLocation.customStartQueryKey);
  params.delete(storeOverviewDrawerLocation.customEndQueryKey);
  params.delete(storeOverviewDrawerLocation.chatQueryKey);
  params.delete(storeOverviewDrawerLocation.chatViewQueryKey);
  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
}

export function isMyFilesDrawerSearch(search: string) {
  return (
    new URLSearchParams(search).get(myFilesDrawerLocation.queryKey) ===
    myFilesDrawerLocation.queryValue
  );
}

export function openMyFilesDrawerSearch(
  search: string,
  options: { preserveResource?: boolean } = {},
) {
  const params = new URLSearchParams(search);
  params.set(
    myFilesDrawerLocation.queryKey,
    myFilesDrawerLocation.queryValue,
  );
  if (!options.preserveResource) {
    params.delete(myFilesDrawerLocation.resourceQueryKey);
  }
  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
}

export function closeMyFilesDrawerSearch(search: string) {
  const params = new URLSearchParams(search);
  params.delete(myFilesDrawerLocation.queryKey);
  params.delete(myFilesDrawerLocation.resourceQueryKey);
  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
}

export function getDesktopShellRouteByPath(pathname: string) {
  const normalizedPath =
    pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

  const routes: Record<DesktopShellRouteId, DesktopShellRouteDefinition> = {
    ...desktopShellRoutes,
    ...desktopShellPageRoutes,
  };

  return Object.entries(routes).find(([, definition]) =>
    definition.match
      ? definition.match(normalizedPath)
      : definition.path === normalizedPath,
  ) as readonly [DesktopShellRouteId, DesktopShellRouteDefinition] | undefined;
}
